import { useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { BidKind } from '@ebalance/proto/bfftypes/v1/bid_kind_pb'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { BidLimitSegmentMeta } from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'
import { UpdateBidLimitMatrixRequestSchema } from '@ebalance/proto/bffuser/v1/bid_limit_service_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  HStack,
  Input,
  PrimaryButton,
  SecondaryButton,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TertiaryButton,
  Text,
  type ToastType,
  toaster,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { ToggleButton } from '~/components/common/ToggleButton'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { isEnterKey } from '~/libs/keyboard'
import { useUpdateBidLimitMatrix } from '~/pages/AdminBidLimits/hooks/useBidLimitSettings'
import type {
  BidLimitValue,
  MatrixDraft,
} from '~/pages/AdminBidLimits/models/bidLimitMatrix'
import {
  MATRIX_SIDES,
  clampMatrixDraft,
  isMatrixDirty,
  matrixDraftToCells,
  matrixKindLabelKey,
  matrixKindsOf,
} from '~/pages/AdminBidLimits/models/bidLimitMatrix'
import {
  bidLimitRangeOf,
  clampBidLimitCellValue,
} from '~/pages/AdminBidLimits/models/bidLimitSchema'
import type { CellKey } from '~/pages/AdminBidLimits/models/cellKey'
import { makeCellKey } from '~/pages/AdminBidLimits/models/cellKey'
import {
  FIRST_SAVE_EXPECTED_UPDATED_AT,
  MARKET_AREAS,
} from '~/pages/AdminBidLimits/models/constants'

// chakra v3 / ark-ui の d.ts 解決問題 (worktree で @ark-ui/react 未 install) により
// toaster が `CreateToasterReturn` → `any` に collapse する。 使用する create の
// 最小シグネチャへ cast して型を復元する (= `as any` ではなく proper 型 cast)。
type ToasterCreate = {
  create: (options: { type?: ToastType; title?: string }) => unknown
}
const typedToaster = toaster as ToasterCreate

/** 一括設定欄は side × kind の列ごとに 1 つ。 エリア別セルとは別の key 空間で持つ。 */
type BulkColumnKey = `${number}|${number}`

const makeBulkColumnKey = (side: Side, kind: BidKind): BulkColumnKey =>
  `${side}|${kind}`

/**
 * 一括設定欄 1 列分の状態。
 *
 * - `text`: 入力欄に表示している値 (エリア別セルと同じく React が持つ)
 * - `broadcast`: この欄が最後に 9 エリアへ配った値 (未配布は `null`)。 確定
 *   (blur / Enter) 時にこれと突き合わせて 「変わった時だけ」 配る (= DOM の
 *   `change` 相当。 `onBlur` は値が変わっていなくても毎回発火する)
 */
type BulkColumnState = {
  text: string
  broadcast: string | null
}

const EMPTY_BULK_COLUMN: BulkColumnState = { text: '', broadcast: null }

/** 売り列グループの先頭列に買い列との区切り線を引く。 */
const sideGroupBorder = (
  side: Side,
  kindIndex: number,
): { borderLeftWidth?: string; borderLeftColor?: string } =>
  side === Side.SELL && kindIndex === 0
    ? { borderLeftWidth: '1px', borderLeftColor: 'Border.midEmphasis' }
    : {}

type Props = {
  segment: BidSegment
  draft: MatrixDraft
  saved: MatrixDraft
  segmentMeta: BidLimitSegmentMeta | undefined
  /**
   * 閲覧 ⇄ 編集の 2 状態 (AC-BDL-006 / AC-BDL-009)。 規制対象の設定なので、
   * カードを開いただけで書き換えられる状態を作らない (spec Discussion
   * 「編集方式」で A: 即時保存 ではなく B: 編集 → 保存 / 変更を破棄 を採用済み、
   * design `admin/bid-limits.html` `_marketEditing` も同じ 2 状態)。
   * 状態は draft と同じく page-level (`useBidLimitMatrixDraft`) が持つ
   * (タブ切替の unmount で失わない・カード見出しの「編集中」表示も同じ値を読む)。
   */
  isEditing: boolean
  /** 「編集」ボタン */
  onStartEdit: () => void
  onSetCell: (key: CellKey, value: BidLimitValue | undefined) => void
  /** draft を saved に戻し、編集モードを解除する */
  onReset: () => void
  /** 保存成功後に saved を更新し、編集モードを解除する */
  onCommit: (newDraft: MatrixDraft) => void
  /** i18n key for the description text. Defaults to the spot-normal description. */
  descriptionKey?: string
  /**
   * workflow-back-bar-readonly AC-17: ワークフローケースが完了済み/削除済みのとき true。
   * true のとき保存・リセットボタンを disabled にする（表示切替系ではなく編集・確定操作のため）。
   */
  isReadonly?: boolean
}

export const BidLimitMatrixCard = ({
  segment,
  draft,
  saved,
  segmentMeta,
  isEditing,
  onStartEdit,
  onSetCell,
  onReset,
  onCommit,
  descriptionKey = 'page:adminBidLimits.matrix.description',
  isReadonly = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { readonlyReason } = useWorkflowLockReasons()
  const { mutateAsync, isPending } = useUpdateBidLimitMatrix()
  const dirty = isMatrixDirty(draft, saved)
  // 列は segment が持つ kind から組む (SPOT_BLOCK はコマ計を持たない。 #8479)
  const kinds = matrixKindsOf(segment)
  const sideColSpan = kinds.length

  /**
   * 一括設定欄の状態 (列ごと)。 エリア別セルと同じく React が値を持つ
   * (= 制御 component)。 非制御にすると focus 時の値退避 ref・クランプ結果の
   * DOM 直接書き戻し・Enter 後の基準値再シードが揃って必要になる。
   */
  const [bulkColumns, setBulkColumns] = useState<
    ReadonlyMap<BulkColumnKey, BulkColumnState>
  >(() => new Map())

  const bulkColumnOf = (side: Side, kind: BidKind): BulkColumnState =>
    bulkColumns.get(makeBulkColumnKey(side, kind)) ?? EMPTY_BULK_COLUMN

  const setBulkColumn = (
    side: Side,
    kind: BidKind,
    next: BulkColumnState,
  ): void => {
    setBulkColumns((prev) =>
      new Map(prev).set(makeBulkColumnKey(side, kind), next),
    )
  }

  /**
   * 保存が押せない理由。 押せる時は `undefined` を返し、 `ButtonTooltip` 側で
   * 吹き出しごと出さない。
   *
   * 範囲外の値は「入力エラー」ではない — 確定時に自動クランプするので
   * (AC-BDL-007)、 値域を理由に保存を止めることはない。
   *
   * 変更が無い時は理由を出さない (非活性にするだけ)。 理由が画面から分かるため
   * (conventions.md「操作できない時のボタンの扱い」、 UI 辞書 action-reason.md)。
   */
  const saveDisabledReason = readonlyReason

  /** 「編集」ボタンが押せない理由 (閲覧モードのみ表示)。 */

  /**
   * 「変更を破棄」(AC-BDL-009): draft を saved に戻し、編集モードを解除する
   * (どちらも `onReset` = 親 hook の `resetDraft` が行う)。 一括欄も初期化する
   * (design `_resetMarket` の `bulkState[market] = {}`。 残すと次の編集で同じ値を
   * 打ち直しても「配布済み」と見なされ 9 セルへ配られない)。
   */
  const handleDiscard = (): void => {
    onReset()
    setBulkColumns(new Map())
  }

  const handleInputChange = (
    areaCode: number,
    side: Side,
    kind: BidKind,
    rawValue: string,
  ): void => {
    const key = makeCellKey(areaCode, side, kind)
    if (rawValue === '') {
      // 空白 = undecided (セル削除)
      onSetCell(key, undefined)
      return
    }
    const num = parseFloat(rawValue)
    if (!isNaN(num)) {
      onSetCell(key, { kind: 'limited', value: num })
    }
  }

  /**
   * 確定時に範囲外の値を範囲内へクランプする (AC-BDL-007)。
   *
   * design `admin/bid-limits.html` の `bindLimitInputs` が `input` ではなく
   * **`change` (= 確定)** に束ねているのと同じ理由で、 キーストロークごとには
   * 丸めない。 打っている途中で丸めると 「0.05」 の先頭の 「0」 が 0.1 に化けて
   * 入力できなくなる。 DOM の `change` は blur だけでなく Enter でも発火するので、
   * 呼び出し側は blur と Enter の両方に結線する (どちらから来ても冪等)。
   */
  const handleInputCommit = (
    areaCode: number,
    side: Side,
    kind: BidKind,
  ): void => {
    const key = makeCellKey(areaCode, side, kind)
    const current = draft.cells.get(key)
    // 未決定 (空欄) / 未設定明示 はクランプ対象外 (= 制限なしのまま)。
    if (current?.kind !== 'limited') return
    const clamped = clampBidLimitCellValue(current.value, kind)
    if (clamped === current.value) return
    onSetCell(key, { kind: 'limited', value: clamped })
  }

  const handleUnsetToggle = (
    areaCode: number,
    side: Side,
    kind: BidKind,
  ): void => {
    if (!isEditing) return
    const key = makeCellKey(areaCode, side, kind)
    const current = draft.cells.get(key)
    if (current?.kind === 'unlimited') {
      // unlimited → undecided (セル削除)
      onSetCell(key, undefined)
    } else {
      // limited or undecided → unlimited
      onSetCell(key, { kind: 'unlimited' })
    }
  }

  /**
   * 一括設定は**前回配った値から変わった確定 (blur / Enter) 時にだけ**
   * クランプ済みの値を 9 エリアへ配る (AC-BDL-007。 design
   * `admin/bid-limits.html` の `bindBulkInputs` も `change` の 1 本だけで書く)。
   *
   * - 打鍵ごとに配らない: 「12000 と打つ → 空に戻す → 確定」 でクランプを通らない
   *   生値が 9 エリアに残ってしまう (空文字は確定処理も素通りするため後から直せない)
   * - 前回配った値と同じなら配らない: 一括欄を素通りしただけで前回値が
   *   再 broadcast され、 その後に個別で直したセルが巻き戻る
   *
   * 比較の基準は 「focus 時点の入力値」 ではなく 「この欄が最後に配った値」。
   * 前者だと一括未設定・空欄化のあとに同じ値を打ち直しても 「変わっていない」
   * と見なされ、 9 セルが黙って元のまま残る。
   */
  const handleBulkCommit = (side: Side, kind: BidKind): void => {
    const { text, broadcast } = bulkColumnOf(side, kind)
    const rawValue = text.trim()
    if (rawValue === '') {
      // 空欄は配らない。 ただし配布済みの記録は落として、 次に同じ値を打ち直した
      // 時に無反応にならないようにする。
      if (broadcast !== null) setBulkColumn(side, kind, EMPTY_BULK_COLUMN)
      return
    }
    if (rawValue === broadcast) return
    const num = parseFloat(rawValue)
    if (isNaN(num)) return
    const clamped = clampBidLimitCellValue(num, kind)
    // クランプ結果は入力欄の表示にも入れる (= 表示と反映値をずらさない)。
    const clampedText = String(clamped)
    setBulkColumn(side, kind, { text: clampedText, broadcast: clampedText })
    for (const area of MARKET_AREAS) {
      onSetCell(makeCellKey(area.code, side, kind), {
        kind: 'limited',
        value: clamped,
      })
    }
  }

  const handleBulkUnset = (side: Side, kind: BidKind): void => {
    if (!isEditing) return
    // design `admin/bid-limits.html` は未設定ボタンで一括欄も空にする。 前値が
    // 残ると、 同じ値を打ち直しても 「変わっていない」 と見なされて 9 セルが
    // 未設定のまま黙って残る。
    setBulkColumn(side, kind, EMPTY_BULK_COLUMN)
    for (const area of MARKET_AREAS) {
      const key = makeCellKey(area.code, side, kind)
      onSetCell(key, { kind: 'unlimited' })
    }
  }

  const handleSave = async (): Promise<void> => {
    // 保存境界のクランプ (AC-BDL-007)。 SoT (mock-app `MarkLimitEdit` /
    // design `_clampLimitValue`) は書き込み経路が 1 本で、 状態に範囲外の値が
    // 存在しない。 実装は打鍵中だけ生値を許す (打鍵ごとに丸めると 「0.05」 が
    // 打てない) ので、 確定 (blur / Enter) を経ずに保存へ来た値もここで必ず
    // 範囲内へ入れてから送る。
    const clampedDraft = clampMatrixDraft(draft)
    for (const [key, value] of clampedDraft.cells) {
      const before = draft.cells.get(key)
      if (
        before?.kind === 'limited' &&
        value.kind === 'limited' &&
        before.value !== value.value
      ) {
        onSetCell(key, value)
      }
    }
    const cells = matrixDraftToCells(clampedDraft, segment)
    try {
      await mutateAsync(
        create(UpdateBidLimitMatrixRequestSchema, {
          bidSegment: segment,
          cells,
          // 初回保存 (segmentMeta 未取得) は epoch 番兵を送る (= backend が None 扱い)。
          expectedUpdatedAt:
            segmentMeta?.updatedAt ?? FIRST_SAVE_EXPECTED_UPDATED_AT,
        }),
      )
      // 保存成功後は閲覧モードへ戻る (AC-BDL-006「編集 → 保存」のセッションが
      // 閉じる。 `onCommit` が解除する)。 失敗時はユーザーがそのまま再試行
      // できるよう編集モードを保つ。
      onCommit(clampedDraft)
      setBulkColumns(new Map())
      typedToaster.create({
        type: 'success',
        title: t('page:adminBidLimits.matrix.saveSuccess'),
      })
    } catch {
      typedToaster.create({
        type: 'error',
        title: t('page:adminBidLimits.matrix.saveError'),
      })
    }
  }

  /** 列の名前 (「買い 上限価格」)。 入力欄のアクセシブルな名前に使う。 */
  const columnLabel = (side: Side, kind: BidKind): string => {
    const sideLabel =
      side === Side.BUY
        ? t('page:adminBidLimits.matrix.buy')
        : t('page:adminBidLimits.matrix.sell')
    return `${sideLabel} ${t(matrixKindLabelKey(kind))}`
  }

  /**
   * 閲覧モードではセルを入力欄に見せず、 未設定トグル・一括未設定ボタンを
   * 隠す (design `admin/bid-limits.html` L28-33「押せるのに変えられない、 を
   * 作らない」)。 `visibility: hidden` なので列幅は編集モードと変わらない。
   * 入力できないこと自体は `disabled` が担い、 ここは見た目だけを持つ。
   */
  const viewModeInputStyle: { borderColor?: string; background?: string } =
    isEditing ? {} : { borderColor: 'transparent', background: 'transparent' }
  const viewModeHidden = isEditing ? undefined : 'hidden'

  /** 一括設定行の入力欄 (+ 一括未設定ボタン)。 */
  const renderBulkInput = (side: Side, kind: BidKind): React.ReactElement => {
    const { text } = bulkColumnOf(side, kind)
    const { max, step } = bidLimitRangeOf(kind)
    return (
      <HStack gap={1}>
        <Input
          width="80px"
          textAlign="right"
          {...viewModeInputStyle}
          aria-label={`${t('page:adminBidLimits.matrix.bulk')} ${columnLabel(side, kind)}`}
          disabled={!isEditing}
          max={max}
          min={0}
          onBlur={() => handleBulkCommit(side, kind)}
          onChange={(e) => {
            setBulkColumn(side, kind, {
              ...bulkColumnOf(side, kind),
              text: e.target.value,
            })
          }}
          onKeyDown={(e) => {
            if (isEnterKey(e)) handleBulkCommit(side, kind)
          }}
          placeholder={t('page:adminBidLimits.matrix.bulkPlaceholder')}
          size="sm"
          step={step}
          type="number"
          value={text}
        />
        <ButtonTooltip content={t('page:adminBidLimits.matrix.bulkUnset')}>
          {/* `size="xs"` は高さを行に合わせるためのもの。 文字は表と同じ md
              (design `.ecl-table td.cell-actions .btn` と同じ作法)。 */}
          <TertiaryButton
            fontSize="md"
            visibility={viewModeHidden}
            aria-label={t('page:adminBidLimits.matrix.bulkUnset')}
            disabled={!isEditing}
            onClick={() => handleBulkUnset(side, kind)}
            size="xs"
            title={t('page:adminBidLimits.matrix.bulkUnset')}
          >
            —
          </TertiaryButton>
        </ButtonTooltip>
      </HStack>
    )
  }

  /** エリア別セルの入力欄 (+ 未設定明示トグル)。 */
  const renderAreaCellInput = (
    areaCode: number,
    side: Side,
    kind: BidKind,
  ): React.ReactElement => {
    const cellVal = draft.cells.get(makeCellKey(areaCode, side, kind))
    const isUnlimited = cellVal?.kind === 'unlimited'
    const numVal = cellVal?.kind === 'limited' ? String(cellVal.value) : ''
    // 値域と刻みは同じ SoT から出す (別出所だと入力欄の刻みと確定時の丸め桁が
    // 黙ってずれる)。
    const { max, step } = bidLimitRangeOf(kind)
    const areaLabel = t(
      MARKET_AREAS.find((a) => a.code === areaCode)?.nameKey ?? '',
    )
    return (
      <HStack gap={1}>
        <Input
          width="80px"
          textAlign="right"
          {...viewModeInputStyle}
          // 未設定 (制限なし) の地は閲覧モードでも残す (design も inline style で
          // 持ち、 閲覧モードの透明化より優先される)。
          background={
            isUnlimited ? 'Surface.tertiary' : viewModeInputStyle.background
          }
          aria-label={`${areaLabel} ${columnLabel(side, kind)}`}
          disabled={isUnlimited || !isEditing}
          max={max}
          min={0}
          onBlur={() => handleInputCommit(areaCode, side, kind)}
          onChange={(e) =>
            handleInputChange(areaCode, side, kind, e.target.value)
          }
          onKeyDown={(e) => {
            if (isEnterKey(e)) handleInputCommit(areaCode, side, kind)
          }}
          placeholder={
            isUnlimited ? t('page:adminBidLimits.matrix.unsetPlaceholder') : '—'
          }
          size="sm"
          step={step}
          type="number"
          value={numVal}
        />
        {/* 文字だけのボタンなので、 同ファイルの一括解除ボタンと同じく DS
            Tooltip で名前を出す。 `title` は Tooltip 非対応環境向けの保険
            (2026-08-17 デザイナー既定)。 */}
        <ButtonTooltip content={t('page:adminBidLimits.matrix.unsetExplicit')}>
          <ToggleButton
            fontSize="md"
            visibility={viewModeHidden}
            aria-label={t('page:adminBidLimits.matrix.unsetExplicit')}
            disabled={!isEditing}
            onClick={() => handleUnsetToggle(areaCode, side, kind)}
            pressed={isUnlimited}
            size="xs"
            title={t('page:adminBidLimits.matrix.unsetExplicit')}
          >
            —
          </ToggleButton>
        </ButtonTooltip>
      </HStack>
    )
  }

  return (
    <Box>
      {/* 8080 のカード補足は 14px (md)。 12px (sm) だと表の数値から沈んで読めない。 */}
      <Text marginBottom={3} color="Text.midEmphasis" fontSize="md">
        {t(descriptionKey)}
      </Text>
      {/* design `.ecl-table-wrap` (`css/components.css`):
          `border: 1px solid var(--border-low); border-radius: var(--radius-lg);
           background: var(--surface-primary); overflow-x: auto`。
          実装は横スクロールだけ拾っていて枠・角丸・地が無く、表がカード本文に
          裸で置かれていた。同型の先行実装は `MasterDataTable` と
          `AdminLossRate/components/LossRateCard`。
          `overflow="hidden"` は角丸から表の角がはみ出すのを止めるため。
          セル外周には枠を足さない (`design/CLAUDE.md`「table-wrap の枠線と
          セル外周枠線の二重化に注意」)。 */}
      <Box
        overflowX="auto"
        background="Surface.primary"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="lg"
      >
        {/* `size="sm"` は行の詰まり (padding / 高さ) を取るためのもので、
            **文字サイズは md (14px) 固定**にする (2026-09-03 デザイナー指摘
            「Table 内のフォントは全て md」)。 recipe の sm は font-size まで
            12px に落とすため、 prop で md を上書きする (chakra v3 は非レイヤーの
            prop 宣言が `@layer recipes` に常に勝つ)。
            design も同じ考え方で、 `.ecl-table` は md、 行内ボタンは
            「font-size は md 維持、 height のみ 40 → 32 に縮める」と
            `css/components.css:1182-1189` に明記されている。 */}
        <TableRoot
          fontSize="md"
          size="sm"
          style={{ fontVariantNumeric: 'tabular-nums', width: '100%' }}
        >
          <TableHeader>
            <TableRow>
              <TableColumnHeader verticalAlign="bottom" rowSpan={2}>
                {t('page:adminBidLimits.matrix.area')}
              </TableColumnHeader>
              {/* 買/売 列見出しの色分け。
                  [2026-09-02] chakra パレット (`blue.500` / `orange.500`) から
                  **DS の売買 token** へ置換した。2026-07-05 に「palette-scale
                  ベースライン、semantic token への置換は別タスク」として容認して
                  いたもので、その別タスクをここで回収している。
                  DS は `Text.bid` (#007d80) / `Text.offer` (#b52020) を持ち、
                  design `admin/bid-limits.html` の `--text-bid` / `--text-offer` と
                  同値。汎用パレットの blue / orange は色相ごと別物だった。 */}
              <TableColumnHeader
                color="Text.bid"
                textAlign="center"
                colSpan={sideColSpan}
              >
                {t('page:adminBidLimits.matrix.buy')}
              </TableColumnHeader>
              <TableColumnHeader
                color="Text.offer"
                textAlign="center"
                borderLeftWidth="1px"
                borderLeftColor="Border.midEmphasis"
                colSpan={sideColSpan}
              >
                {t('page:adminBidLimits.matrix.sell')}
              </TableColumnHeader>
            </TableRow>
            <TableRow>
              {MATRIX_SIDES.flatMap((side) =>
                kinds.map((kind, i) => (
                  <TableColumnHeader
                    key={makeBulkColumnKey(side, kind)}
                    textAlign="right"
                    {...sideGroupBorder(side, i)}
                  >
                    {t(matrixKindLabelKey(kind))}
                  </TableColumnHeader>
                )),
              )}
            </TableRow>
            {/* 一括設定行。design `admin/bid-limits.html` の
                `tr.bid-limit-bulk-thead-row th { border-bottom: 2px solid var(--border-mid) }`
                （コメント「一括行は通常の data 行と区別するため bottom border を太く」）。
                地と太字だけだと、データ行にゼブラ（同じ `Surface.secondary`）が
                入った時に見分けが付かなくなるため、下線の格で区別する。
                線は `tr` ではなくセル側に当てる（`tr` に付けても td/th 境界には出ない）。 */}
            <TableRow fontWeight="bold" background="Surface.secondary">
              <TableColumnHeader
                borderBottomWidth="2px"
                borderBottomColor="Border.midEmphasis"
              >
                {t('page:adminBidLimits.matrix.bulk')}
              </TableColumnHeader>
              {MATRIX_SIDES.flatMap((side) =>
                kinds.map((kind, i) => (
                  <TableColumnHeader
                    key={makeBulkColumnKey(side, kind)}
                    textAlign="right"
                    borderBottomWidth="2px"
                    borderBottomColor="Border.midEmphasis"
                    {...sideGroupBorder(side, i)}
                  >
                    {renderBulkInput(side, kind)}
                  </TableColumnHeader>
                )),
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* ゼブラ。design `.ecl-table tbody tr:nth-child(even)
                { background: var(--surface-secondary) }` 準拠で、本表にも無条件で
                効くべきもの。同じ実装が `MasterDataTable` / `SupplyBilateral` /
                `planSubmit/PlanTable` にある。
                一括設定行も同じ `Surface.secondary` だが、あちらは下線 2px で
                区別する (下記)。 */}
            {MARKET_AREAS.map((area, idx) => (
              <TableRow
                key={area.code}
                background={idx % 2 === 1 ? 'Surface.secondary' : undefined}
              >
                <TableCell>{t(area.nameKey)}</TableCell>
                {MATRIX_SIDES.flatMap((side) =>
                  kinds.map((kind, i) => (
                    <TableCell
                      key={makeBulkColumnKey(side, kind)}
                      textAlign="right"
                      {...sideGroupBorder(side, i)}
                    >
                      {renderAreaCellInput(area.code, side, kind)}
                    </TableCell>
                  )),
                )}
              </TableRow>
            ))}
          </TableBody>
        </TableRoot>
      </Box>
      {/* footer: 閲覧モードは「編集」ボタンのみ (AC-BDL-006)。 編集モードは
          sticky footer に「保存」「変更を破棄」ボタンを表示する (AC-BDL-006/009)。
          design `admin/bid-limits.html` の「編集」ボタンだけの時は固定しない
          コメント通り、 sticky にするのは編集モード中のみ (閲覧モードで常時
          sticky にすると内容の無い帯が残る)。 */}
      <HStack
        position={isEditing ? 'sticky' : undefined}
        zIndex={5}
        bottom={0}
        justify="flex-end"
        gap={2}
        marginTop={4}
        // 下端固定バーの地は全画面 **Surface.primary** で統一する
        // (デザイナー指摘 2026-08-27「table の下に floating で固定表示する bar が
        //  あるが、 デザインが混在。 DS の BottomActionBar を元にしたことを残したく、
        //  背景色は全て primary に統一したい」)。 DS BottomActionBar 由来の構造
        // (上罫線 + 影 + 内側 padding) はそのまま残し、 地だけを揃える。
        background="Surface.primary"
        borderTopWidth="1px"
        borderTopColor="Border.lowEmphasis"
        paddingX={4}
        paddingY={3}
      >
        {isEditing ? (
          <>
            {/* 「変更を破棄」は編集中は常に押せる (未編集でもモードを降りる手段が
                要る。 design `admin/bid-limits.html` `_refreshMatrixCardState`)。
                書き込みを伴わない local 操作なので読み取り専用でも止めない。 */}
            <TertiaryButton disabled={isPending} onClick={handleDiscard}>
              {t('page:adminBidLimits.matrix.discard')}
            </TertiaryButton>
            {/* 押せない理由を吹き出しで出す (UI 辞書 action-reason.md の区分 2。
                native の title は使わない)。 */}
            <ButtonTooltip content={saveDisabledReason}>
              <PrimaryButton
                disabled={!dirty || isReadonly}
                loading={isPending}
                onClick={handleSave}
              >
                {t('common:action.save')}
              </PrimaryButton>
            </ButtonTooltip>
          </>
        ) : (
          <ButtonTooltip content={readonlyReason}>
            <SecondaryButton
              disabled={isReadonly}
              onClick={() => {
                if (!isReadonly) onStartEdit()
              }}
            >
              {t('common:action.edit')}
            </SecondaryButton>
          </ButtonTooltip>
        )}
      </HStack>
    </Box>
  )
}
